import React from 'react';
import { Form as AntForm, FormProps as AntFormProps, FormItemProps as AntFormItemProps } from 'antd';
import { Form as CompatibleForm } from '@ant-design/compatible';
import '@ant-design/compatible/assets/index.css';
import { createUseStyles } from 'react-jss';
import { ThemeType } from 'src/theming/jssTypes';

export interface FormProps extends AntFormProps {
  className?: string;
}

export interface FormItemProps extends AntFormItemProps {
  className?: string;
}

const useStyles = createUseStyles((theme: ThemeType) => ({
  form: {
    backgroundColor: theme.color.semantic.background.neutral.secondary,
    '& .ant-form-item-label > label': {
      color: theme.color.semantic.text.primary,
      fontSize: theme.typography.fontSize.sm,
      fontWeight: theme.typography.fontWeight.medium,
      lineHeight: theme.typography.lineHeight.normal,
      '&.ant-form-item-required': {
        '&::before': {
          color: theme.color.semantic.brand.danger.primary,
        },
      },
    },
    '& .ant-form-item-optional': {
      color: theme.color.semantic.text.secondary,
      fontSize: theme.typography.fontSize.sm,
      fontWeight: theme.typography.fontWeight.regular,
      lineHeight: theme.typography.lineHeight.tight,
    },
    '& .ant-form-item-explain-error': {
      color: theme.color.semantic.brand.danger.primary,
      fontSize: theme.typography.fontSize.xs,
    },
    '& .ant-form-item-explain-warning': {
      color: theme.color.semantic.accent.orange.strong,
      fontSize: theme.typography.fontSize.xs,
    },
    '& .ant-form-item-explain-success': {
      color: theme.color.semantic.brand.default.primary,
      fontSize: theme.typography.fontSize.xs,
    },
    '& .ant-form-item-has-error': {
      '& .ant-input': {
        borderColor: theme.color.semantic.brand.danger.primary,
      },
      '& .ant-input-number': {
        borderColor: theme.color.semantic.brand.danger.primary,
      },
      '& .ant-select-selector': {
        borderColor: `${theme.color.semantic.brand.danger.primary} !important`,
      },
      '& .ant-picker': {
        borderColor: theme.color.semantic.brand.danger.primary,
      },
    },
    '& .ant-form-item-has-warning': {
      '& .ant-input': {
        borderColor: theme.color.semantic.accent.orange.strong,
      },
      '& .ant-input-number': {
        borderColor: theme.color.semantic.accent.orange.strong,
      },
      '& .ant-select-selector': {
        borderColor: `${theme.color.semantic.accent.orange.strong} !important`,
      },
      '& .ant-picker': {
        borderColor: theme.color.semantic.accent.orange.strong,
      },
    },
    '& .ant-form-item-has-success': {
      '& .ant-input': {
        borderColor: theme.color.semantic.border.primary,
      },
      '& .ant-input-number': {
        borderColor: theme.color.semantic.border.primary,
      },
      '& .ant-select-selector': {
        borderColor: `${theme.color.semantic.border.primary} !important`,
      },
      '& .ant-picker': {
        borderColor: `${theme.color.semantic.border.primary} !important`,
      },
      '& .ant-form-item-feedback-icon': {
        color: theme.color.semantic.brand.default.primary,
      },
    },
    '& .ant-input': {
      backgroundColor: theme.color.semantic.background.neutral.secondary,
      color: `${theme.color.semantic.text.primary} !important`,
      borderColor: theme.color.semantic.border.primary,
      borderWidth: theme.border.width.sm,
      borderRadius: theme.radius.sm,
      fontWeight: theme.typography.fontWeight.regular,
      lineHeight: theme.typography.lineHeight.normal,
      padding: `${theme.spacing[0]} ${theme.spacing[4]}`,
      fontSize: theme.typography.fontSize.sm,
      boxShadow: 'none !important',
      '&::placeholder': {
        color: theme.color.semantic.text.soft,
      },
      '&:hover': {
        backgroundColor: theme.color.semantic.background.neutral.secondary,
        borderColor: theme.color.semantic.brand.default.soft,
        boxShadow: 'none !important',
      },
      '&:focus, &:active': {
        backgroundColor: theme.color.semantic.background.neutral.secondary,
        borderColor: theme.color.semantic.brand.default.moderate,
        boxShadow: 'none !important',
      },
    },
    '& input.ant-input': {
      height: '32px',
    },
    '& textarea.ant-input': {
      padding: `${theme.spacing[2]} ${theme.spacing[4]}`,
    },
    '& .ant-input:disabled, & .ant-input[disabled]': {
      backgroundColor: `${theme.color.semantic.background.neutral.primary} !important`,
      color: `${theme.color.semantic.text.soft} !important`,
      borderColor: `${theme.color.semantic.border.primary} !important`,
      cursor: 'not-allowed !important',
      '&:hover, &:focus, &:active': {
        backgroundColor: `${theme.color.semantic.background.neutral.primary} !important`,
        borderColor: `${theme.color.semantic.border.primary} !important`,
        boxShadow: 'none !important',
      },
    },
    '& .ant-input-affix-wrapper-disabled, & .ant-input-affix-wrapper[disabled]': {
      backgroundColor: `${theme.color.semantic.background.neutral.primary} !important`,
      borderColor: `${theme.color.semantic.border.primary} !important`,
      cursor: 'not-allowed !important',
      '& .ant-input[disabled]': {
        backgroundColor: 'transparent !important',
      },
      '&:hover, &:focus, &:active': {
        backgroundColor: `${theme.color.semantic.background.neutral.primary} !important`,
        borderColor: `${theme.color.semantic.border.primary} !important`,
        boxShadow: 'none !important',
      },
    },
    '& .ant-input-number': {
      height: '32px',
      backgroundColor: theme.color.semantic.background.neutral.secondary,
      borderColor: theme.color.semantic.border.primary,
      borderWidth: theme.border.width.sm,
      borderRadius: theme.radius.sm,
      '& .ant-input-number-input': {
        color: theme.color.semantic.text.primary,
        fontSize: theme.typography.fontSize.sm,
        fontWeight: theme.typography.fontWeight.regular,
        lineHeight: theme.typography.lineHeight.normal,
        padding: `${theme.spacing[0]} ${theme.spacing[4]}`,
        backgroundColor: 'transparent',
        '&::placeholder': {
          color: theme.color.semantic.text.soft,
        },
      },
      '&:hover': {
        backgroundColor: theme.color.semantic.background.neutral.secondary,
        borderColor: theme.color.semantic.brand.default.soft,
      },
      '&:focus, &.ant-input-number-focused': {
        backgroundColor: theme.color.semantic.background.neutral.secondary,
        borderColor: theme.color.semantic.brand.default.moderate,
        boxShadow: 'none !important',
      },
    },
    '& .ant-input-number.ant-input-number-disabled': {
      backgroundColor: `${theme.color.semantic.background.neutral.subtle} !important`,
      opacity: `${theme.opacity[70]} !important`,
      borderColor: `${theme.color.semantic.border.primary} !important`,
      cursor: 'not-allowed !important',
      '& .ant-input-number-input': {
        color: `${theme.color.semantic.text.soft} !important`,
        cursor: 'not-allowed !important',
      },
      '&:hover, &:focus': {
        borderColor: `${theme.color.semantic.border.primary} !important`,
        boxShadow: 'none !important',
      },
    },
    '& .ant-select .ant-select-selector': {
      height: '32px !important',
      backgroundColor: `${theme.color.semantic.background.neutral.secondary} !important`,
      borderColor: `${theme.color.semantic.border.primary} !important`,
      borderWidth: theme.border.width.sm,
      borderRadius: `${theme.radius.sm} !important`,
      fontSize: theme.typography.fontSize.sm,
      fontWeight: theme.typography.fontWeight.regular,
      padding: `${theme.spacing[0]} ${theme.spacing[3]} !important`,
      '& .ant-select-selection-placeholder': {
        color: theme.color.semantic.text.soft,
      },
      '& .ant-select-selection-item': {
        color: theme.color.semantic.text.primary,
        fontSize: theme.typography.fontSize.sm,
        fontWeight: theme.typography.fontWeight.regular,
      },
      '& .ant-select-selection-search-input': {
        fontSize: theme.typography.fontSize.sm,
        color: theme.color.semantic.text.primary,
      },
    },
    '& .ant-select:not(.ant-select-disabled):hover .ant-select-selector': {
      backgroundColor: `${theme.color.semantic.background.neutral.secondary} !important`,
      borderColor: `${theme.color.semantic.brand.default.soft} !important`,
    },
    '& .ant-select-focused .ant-select-selector': {
      backgroundColor: `${theme.color.semantic.background.neutral.secondary} !important`,
      borderColor: `${theme.color.semantic.brand.default.moderate} !important`,
      boxShadow: 'none !important',
    },
    '& .ant-select-disabled .ant-select-selector': {
      backgroundColor: `${theme.color.semantic.background.neutral.subtle} !important`,
      opacity: `${theme.opacity[70]} !important`,
      color: `${theme.color.semantic.text.soft} !important`,
      borderColor: `${theme.color.semantic.border.primary} !important`,
      cursor: 'not-allowed !important',
      '&:hover, &:focus, &:active': {
        backgroundColor: `${theme.color.semantic.background.neutral.subtle} !important`,
        borderColor: `${theme.color.semantic.border.primary} !important`,
        boxShadow: 'none !important',
      },
    },
    '& .ant-picker': {
      height: '32px',
      backgroundColor: `${theme.color.semantic.background.neutral.secondary} !important`,
      borderColor: `${theme.color.semantic.border.primary} !important`,
      borderWidth: theme.border.width.sm,
      borderRadius: theme.radius.sm,
      padding: `${theme.spacing[0]} ${theme.spacing[4]}`,
      fontSize: theme.typography.fontSize.sm,
      fontWeight: theme.typography.fontWeight.regular,
      lineHeight: theme.typography.lineHeight.normal,
      '& .ant-picker-input > input': {
        color: theme.color.semantic.text.primary,
        fontSize: theme.typography.fontSize.sm,
        '&::placeholder': {
          color: theme.color.semantic.text.soft,
        },
      },
      '& .ant-picker-suffix': {
        color: theme.color.semantic.text.soft,
      },
      '&:hover': {
        backgroundColor: `${theme.color.semantic.background.neutral.secondary} !important`,
        borderColor: `${theme.color.semantic.brand.default.soft} !important`,
      },
      '&.ant-picker-focused': {
        backgroundColor: `${theme.color.semantic.background.neutral.secondary} !important`,
        borderColor: `${theme.color.semantic.brand.default.moderate} !important`,
        boxShadow: 'none !important',
      },
    },
    '& .ant-picker.ant-picker-disabled': {
      backgroundColor: `${theme.color.semantic.background.neutral.subtle} !important`,
      borderColor: `${theme.color.semantic.border.primary} !important`,
      opacity: `${theme.opacity[70]} !important`,
      cursor: 'not-allowed',
      '& .ant-picker-input > input': {
        color: `${theme.color.semantic.text.soft} !important`,
        cursor: 'not-allowed',
      },
      '&:hover': {
        borderColor: `${theme.color.semantic.border.primary} !important`,
      },
    },
  },
  formHorizontal: {
    padding: `${theme.spacing[3]} ${theme.spacing[0]}`,
    '& .ant-form-item-label': {
      textAlign: 'right',
    },
  },
  formVertical: {
    padding: `${theme.spacing[3]} ${theme.spacing[0]}`,
    '& .ant-form-item-label': {
      textAlign: 'left',
    },
    '& .ant-form-item-explain': {
      fontSize: theme.typography.fontSize.xs,
      lineHeight: theme.typography.lineHeight.tight,
    },
  },
  formInline: {
    padding: `${theme.spacing[0]} ${theme.spacing[2]}`,
    '& .ant-form-item': {
      marginRight: theme.spacing[2],
    },
    '& .ant-form-item-explain': {
      fontSize: theme.typography.fontSize.xs,
      lineHeight: theme.typography.lineHeight.tight,
    },
  },
  formDisabled: {
    backgroundColor: theme.color.semantic.background.neutral.soft,
    '& .ant-form-item-label > label': {
      color: theme.color.semantic.text.soft,
    },
  },
}));

const FormInner: React.FC<FormProps> = ({
  className = '',
  layout = 'vertical',
  ...rest
}) => {
  const classes = useStyles();

  const getLayoutClass = () => {
    switch (layout) {
      case 'horizontal':
        return classes.formHorizontal;
      case 'inline':
        return classes.formInline;
      case 'vertical':
      default:
        return classes.formVertical;
    }
  };

  const combinedClassName = `${classes.form} ${getLayoutClass()} ${className}`.trim();

  return <AntForm className={combinedClassName} layout={layout} {...rest} />;
};

export const Form = Object.assign(FormInner, {
  Item: AntForm.Item,
  List: AntForm.List,
  ErrorList: AntForm.ErrorList,
  useForm: AntForm.useForm,
  useWatch: AntForm.useWatch,
  Provider: AntForm.Provider,
  create: CompatibleForm.create,
});

export default Form;
